import { useState, useCallback, useEffect } from "react";

import { AccessTokenError } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { useStripeAccountContext } from "../../contexts/StripeAccountContext";
import { trpc } from "../../helpers/trpc";
import { useTranslate } from "../../hooks/useTranslate";

import { DashboardButtonProps } from "@/dashboard-design-system/Button";
import { isErrorWithCode } from "@/helpers/error";

export interface BankOnboardingButtonProps extends DashboardButtonProps {
  isCustomElement?: boolean;
  disabled?: boolean;
}

export const useRedirectToStripeButton = () => {
  const { refetchOrganization } = useStripeAccountContext();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const { getStripeAccountLink } = trpc.useUtils();

  // When org signup, the stripe account is created in async. We need to refetch the organization be sure the following redirectToStripe function
  // does not call createStripeAccountWithLink if account has already a stripeCustomAccountId
  useEffect(() => {
    refetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRedirectionLink = useCallback(async () => {
    setIsLoading(true);
    try {
      return await getStripeAccountLink.fetch();
    } catch (error) {
      if (isErrorWithCode(error) && error.code === AccessTokenError.UrlInvalid) {
        enqueueSnackbar(t("dashboard", "invalidDomainUrl"), { vibe: "danger" });
      } else {
        enqueueSnackbar(t("common", "unknownError"), { vibe: "danger" });
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  }, [getStripeAccountLink, enqueueSnackbar, t]);

  return { getRedirectionLink, isLoading, setIsLoading };
};
